import { useMemo, useState } from "react";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useTranslation } from "@i18n";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@Services/axiosWrapper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "@Services/constants";
import type { CreateViewResponse, UpdateViewResponse, LayoutData } from "@ServerTypes/layoutView/layoutView.schema";
import DragListComponent from "@CubeComponents/html/dragList";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import IconComponent from "@CubeComponents/icons";
import { PROJECT_DASHBOARD_RESOURCE_TYPE } from "@Helpers/constants";
import Text from "@CubeComponents/html/Text/Text";
import { getLayoutModuleIds } from "@Features/projects/utils/layoutModules";

export interface SidebarIcon {
  id: string;
  name: string;
  svg: string;
  allow?: boolean;
}

// A type alias (not an interface) so it satisfies the DragListComponent's
// `T extends Record<string, unknown>` constraint — interfaces lack an implicit index signature.
type ModuleItem = {
  text: string;
  value: string;
  svg: string;
};

interface ConfigureLayoutModalProps {
  /** "mid" position sidebar icons used as the pool of available modules. */
  midIcons: SidebarIcon[];
  projectId: string | null;
  /** Whether a newly created layout is shared (chosen from the Create menu). Ignored when editing. */
  isShared: boolean;
  /** When set, the modal opens in "Configure" (edit) mode pre-filled with this layout's data. */
  layoutToEdit?: LayoutData;
  onClose: () => void;
  /** Called with the saved layout record (and whether it was an edit) so the caller can update its list. */
  onSaved: (layout: LayoutData, isEdit: boolean) => void;
}

function ConfigureLayoutModal({
  midIcons,
  projectId,
  isShared,
  layoutToEdit,
  onClose,
  onSaved,
}: ConfigureLayoutModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isEdit = layoutToEdit !== undefined;

  // Full pool of selectable modules, keyed by module id for quick lookup.
  const modulePool = useMemo<ModuleItem[]>(
    () => midIcons.map((icon) => ({ text: icon.name, value: icon.id, svg: icon.svg })),
    [midIcons],
  );

  // In edit mode, seed "Added" from the layout's saved modules (in order); "Available" is the rest.
  // IMPORTANT: never drop a saved module id. Resolve each id against the FULL icon set (not just
  // enabled ones) so a temporarily-disabled module keeps its real label/icon, and fall back to the
  // raw id for genuinely-unknown ones. Otherwise saving a layout while a module is disabled would
  // permanently remove it from the layout (handleSave persists `added.map(v => v.value)`), even
  // after the module is re-enabled.
  const { initialAvailable, initialAdded } = useMemo(() => {
    const savedIds = getLayoutModuleIds(layoutToEdit);
    const iconById = new Map(midIcons.map((icon) => [icon.id, icon]));
    const added: ModuleItem[] = savedIds.map((id) => {
      const icon = iconById.get(id);
      return icon ? { text: icon.name, value: icon.id, svg: icon.svg } : { text: id, value: id, svg: "" };
    });
    const addedIds = new Set(added.map((module) => module.value));
    const available = modulePool.filter((module) => !addedIds.has(module.value));
    return { initialAvailable: available, initialAdded: added };
  }, [midIcons, modulePool, layoutToEdit]);

  const [name, setName] = useState<string>(layoutToEdit?.name ?? "");
  const [available, setAvailable] = useState<ModuleItem[]>(initialAvailable);
  const [added, setAdded] = useState<ModuleItem[]>(initialAdded);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [applyToAllProjects, setApplyToAllProjects] = useState<boolean>(false);
  const [isLayoutSaving, setIsLayoutSaving] = useState(false);

  const isLayoutValid = name.trim().length > 0 && added.length > 0;

  // In edit ("Configure") mode, only enable Save once something actually changed — the name or
  // the ordered module list — so we don't fire an update API call with no modifications.
  const addedModuleIds = added.map((item) => item.value);
  const hasChanges =
    name.trim() !== (layoutToEdit?.name ?? "").trim() ||
    addedModuleIds.length !== initialAdded.length ||
    addedModuleIds.some((id, index) => id !== initialAdded[index]?.value);
  const canSave = isLayoutValid && (!isEdit || hasChanges);

  const renderModuleRow = (item: ModuleItem) => (
    <span className="flex items-center gap-2">
      {item.svg ? <IconComponent name={item.svg} width="1rem" height="1rem" /> : null}
      {item.text}
    </span>
  );

  const handleSave = async () => {
    if (!isLayoutValid || isLayoutSaving) return;
    setIsLayoutSaving(true);
    try {
      const modules = added.map((item) => item.value);
      if (layoutToEdit) {
        const response = await axiosWrapper.post<UpdateViewResponse>(`${getBackendUrl()}/layoutView/updateView`, {
          id: layoutToEdit._id,
          updateData: {
            name: name.trim(),
            data: { ...(layoutToEdit.data ?? {}), modules },
            applyToAllProjects,
          },
        });
        if (response.data.status) {
          const updated: LayoutData = {
            ...layoutToEdit,
            name: name.trim(),
            data: { ...(layoutToEdit.data ?? {}), modules },
          };
          dispatch(
            showToastNotification({
              content: t("designviewer_layout_updated", { layoutName: updated.name }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          onSaved(updated, true);
          onClose();
        } else {
          dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
        }
        return;
      }

      const response = await axiosWrapper.post<CreateViewResponse>(`${getBackendUrl()}/layoutView/createView`, {
        createData: {
          resourceType: PROJECT_DASHBOARD_RESOURCE_TYPE,
          name: name.trim(),
          isShared,
          projectId,
          data: { modules },
          applyToAllProjects,
        },
      });
      const created = response.data.data;
      if (response.data.status && created) {
        dispatch(
          showToastNotification({
            content: t("designviewer_layout_created", { layoutName: created.name }),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        onSaved(created, false);
        onClose();
      } else {
        dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
      }
    } catch {
      // Network / unexpected failure — surface it instead of leaving the modal frozen with no feedback.
      dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
    } finally {
      setIsLayoutSaving(false);
      setShowConfirmationModal(false);
      setApplyToAllProjects(false);
    }
  };

  const modalContent = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-4 my-4">
        <Text className="text-sm font-normal whitespace-nowrap">{t("Layout Name")}</Text>
        <div className="flex-1">
          <TextFieldComponent
            placeholder={t("Enter Name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="layoutNameInput"
          />
        </div>
      </div>
      <DragListComponent
        data1={available}
        data2={added}
        setData1={setAvailable}
        setData2={setAdded}
        fields={{ text: "text", value: "value" }}
        noItemsText={t("No Modules Added")}
        itemTemplate={renderModuleRow}
        showHint={false}
        showButtons
        showSearch
        allowMultiSelect
        fillHeight
      />
    </div>
  );

  const handleSaveClick = () => {
    if (showConfirmationModal) {
      handleSave();
    } else {
      setShowConfirmationModal(true);
    }
  };

  const footerContent = (
    <div className="flex gap-3">
      <ButtonComponent
        buttonType="gradientButton"
        disabled={!canSave}
        loading={isLayoutSaving}
        onClick={handleSaveClick}
        data-testid="createLayoutSubmit"
      >
        {showConfirmationModal ? t("Set Layout") : t("Save Changes")}
      </ButtonComponent>
      <ButtonComponent
        buttonType="borderButton"
        onClick={() => (showConfirmationModal ? setShowConfirmationModal(false) : onClose())}
        data-testid="createLayoutCancel"
      >
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );
  const confirmationContent = (
    <div className="flex flex-col gap-4 my-4">
      <Text className="text-sm font-normal">{t("Are you sure you want to set this layout as current?")}</Text>
      <div className="flex flex-col gap-1">
        <RadioButton
          name="applyLayoutScope"
          label={t("Apply only for current project")}
          checked={!applyToAllProjects}
          onChange={() => setApplyToAllProjects(false)}
          data-testid="applyCurrentProjectRadio"
        />
        <RadioButton
          name="applyLayoutScope"
          label={t("Apply for all projects")}
          checked={applyToAllProjects}
          onChange={() => setApplyToAllProjects(true)}
          data-testid="applyAllProjectsRadio"
        />
      </div>
    </div>
  );

  const confirmationModal = (
    <ModalStateDialogComponent
      isVisible={showConfirmationModal}
      headerTitle={t("Set Layout as Current")}
      modelContent={confirmationContent}
      footerContent={footerContent}
      onCancel={() => setShowConfirmationModal(false)}
      width="48rem"
    />
  );

  return (
    <>
      <ModalStateDialogComponent
        isVisible
        headerTitle={isEdit ? t("Configure Layout") : t("Create Layout")}
        modelContent={modalContent}
        footerContent={footerContent}
        onCancel={onClose}
        height="75%"
        width="56.25rem"
      />
      {showConfirmationModal && confirmationModal}
    </>
  );
}

export default ConfigureLayoutModal;
